import { ReactNode } from "react";
import { inEmbed } from "util/embed";
import { generateInitials } from "util/generateInitials";
import { safePhoto } from "util/safePhoto";

/**
 * The gray page, one white card, and "Scheduling by Tadpole" under it. In a
 * widget, the card alone, outlined, with the website showing around it.
 */
export function PublicShell({
  width,
  children,
}: {
  width: "narrow" | "wide";
  children: ReactNode;
}) {
  const maxWidth = width === "wide" ? "max-w-[1060px]" : "max-w-[920px]";
  if (inEmbed())
    return (
      <div className="p-px font-sans text-neutral-900">
        <div
          className={`mx-auto overflow-hidden rounded-xl border border-gray-200 bg-white ${maxWidth}`}
        >
          {children}
        </div>
        <p className="pt-3 pb-1 text-center text-xs text-gray-400">
          Scheduling by Tadpole
        </p>
      </div>
    );
  // -mt cancels the app navbar's space (App's .wrapper), as the public form does.
  return (
    <div className="-mt-[85px] min-h-screen bg-[#F4F5F6] font-sans text-neutral-900 sm:px-4 sm:py-9">
      <div
        className={`mx-auto overflow-hidden bg-white sm:rounded-xl sm:border sm:border-gray-200 ${maxWidth}`}
      >
        {children}
      </div>
      <p className="py-5 text-center text-xs text-gray-400">
        Scheduling by Tadpole
      </p>
    </div>
  );
}

/** A round (or rounded square) photo, or initials on the accent's tint. */
export function PersonPhoto({
  url,
  name,
  size,
  accent,
  square = false,
}: {
  url?: string | null;
  name: string;
  size: number;
  accent: string;
  square?: boolean;
}) {
  const src = safePhoto(url);
  const style = { width: size, height: size };
  const shape = square ? "rounded-2xl" : "rounded-full";
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        style={style}
        className={`shrink-0 object-cover ${shape}`}
      />
    );
  }
  return (
    <div
      style={{
        ...style,
        backgroundColor: `${accent}1F`,
        color: accent,
        fontSize: size * 0.34,
      }}
      className={`flex shrink-0 items-center justify-center font-bold ${shape}`}
      aria-hidden
    >
      {generateInitials(name)}
    </div>
  );
}

/**
 * The org's logo, whole: `size` tall and as wide as its shape needs, up to
 * `maxWidth`, never cropped. Without one, its initials in a rounded square.
 */
export function OrgLogo({
  url,
  name,
  size,
  accent,
  maxWidth = size * 4,
}: {
  url?: string | null;
  name: string;
  size: number;
  accent: string;
  maxWidth?: number;
}) {
  const src = safePhoto(url);
  const style = { width: size, height: size };
  return src ? (
    <img
      src={src}
      alt={name}
      style={{ height: size, width: "auto", maxWidth }}
      className="shrink-0 object-contain"
    />
  ) : (
    <div
      style={{ ...style, backgroundColor: accent, fontSize: size * 0.36 }}
      className="flex shrink-0 items-center justify-center rounded-[22%] font-bold text-white"
      aria-hidden
    >
      {generateInitials(name)}
    </div>
  );
}

/** A plain message in place of the page (closed, not found). */
export function PublicMessage({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) {
  return (
    <PublicShell width="narrow">
      <div className="px-6 py-16 text-center">
        <h1 className="text-xl font-bold">{title}</h1>
        {children && <div className="mt-2 text-gray-600">{children}</div>}
      </div>
    </PublicShell>
  );
}
